import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccessibleIcon from "@mui/icons-material/Accessible";
import AddIcon from "@mui/icons-material/Add";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DiscountIcon from "@mui/icons-material/Discount";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import FlagIcon from "@mui/icons-material/Flag";
import FlareIcon from "@mui/icons-material/Flare";
import GroupIcon from "@mui/icons-material/Group";
import InfoIcon from "@mui/icons-material/Info";
import LanguageIcon from "@mui/icons-material/Language";
import PeopleIcon from "@mui/icons-material/People";
import RecommendIcon from "@mui/icons-material/Recommend";
import StyleIcon from "@mui/icons-material/Style";
import UpdateIcon from "@mui/icons-material/Update";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Divider, SvgIconProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import capitalize from "lodash/capitalize";
import groupBy from "lodash/groupBy";
import React, { ComponentType } from "react";

import { ProductAttributeListNode, ProductDataCancellationPolicy, ProductGuideLanguageListNode } from "../../apiHooks";
import { Typography } from "../../components/Typography";
import { useLanguage, useNextTranslation } from "../../hooks";
import { useProductTranslation } from "../../hooks/useProductTranslation";
import { formatDurationFromString, formatDurationToTextAlternative } from "../../utils/formatters/formatDuration";
import { ProductMeta } from "../types";

export type ProductMetaProps = {
    meta: ProductMeta;
    cancellationPolicy?: ProductDataCancellationPolicy;
    isOnRequest?: boolean;
    attributeList?: ProductAttributeListNode[];
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">;

export function ProductMetadataV2({ meta, cancellationPolicy, attributeList, ...props }: ProductMetaProps) {
    const iterator = useMetadataInfoGenerator({ meta, cancellationPolicy, attributeList });
    const [Item1, Item2, Item3] = [iterator.next().value, iterator.next().value, iterator.next().value].filter(
        (component): component is React.JSX.Element => !!component
    );

    return (
        <Wrapper {...props}>
            {Item1}
            <DividerStyled orientation="vertical" />
            {Item2}
            <DividerStyled orientation="vertical" />
            {Item3}
        </Wrapper>
    );
}

const Wrapper = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "start",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const DividerStyled = styled(Divider)({
    alignSelf: "stretch",
    height: "auto",
});

const VOUCHER_LEVEL1 = "Good To Know";
type MetadataInfoParams = Pick<ProductMetaProps, "meta" | "cancellationPolicy" | "attributeList" | "isOnRequest">;

function* useMetadataInfoGenerator(params: MetadataInfoParams) {
    const language = useLanguage();
    const [t] = useNextTranslation("product");

    const { meta, cancellationPolicy, isOnRequest } = params;

    // They are displayed in the order of the following list
    // 1. Duration; 2. Ticket type (eg e-voucher); 3. Languages; 4. Free cancellation
    if (meta.minDuration ?? meta.maxDuration) {
        const formatted = formatDurationFromString(
            language,
            meta.minDuration ?? undefined,
            meta.maxDuration ?? undefined
        );
        if (formatted) yield <MetadataItem Icon={UpdateIcon} text={formatted} />;
    }

    const { voucher, attributeList } = splitVoucherFromAttributeList(params.attributeList);

    if (voucher) {
        const text = capitalize(voucher.name ?? "");
        yield <MetadataItem Icon={DiscountIcon} text={text} />;
    }

    if (meta.guideLanguages && meta.guideLanguages.length > 0) {
        yield <ProductGuideLanguageMetadata languages={meta.guideLanguages} />;
    }

    if (cancellationPolicy?.hasFreeCancellation) {
        yield <MetadataItem Icon={EventBusyIcon} text={t("label.freeCancellation")} />;
    }

    if (meta.startTime) {
        yield <MetadataItem Icon={AccessTimeIcon} text={meta.startTime} />;
    }

    if (meta.fitnessLevel ?? meta.difficultyLevel) {
        const value = [meta.difficultyLevel, meta.fitnessLevel].filter((item) => item).join(", ");
        yield <MetadataItem Icon={FitnessCenterIcon} text={value} />;
    }

    if (meta.cultureShockLevel) {
        // TODO: Find an example for Culture Shock Level, to make sure it make sense without a label | in addition consider using different icon
        yield <MetadataItem Icon={InfoIcon} text={meta.cultureShockLevel} />;
    }

    if (meta.bestTimeToVisit) {
        yield <MetadataItem Icon={UpdateIcon} text={meta.bestTimeToVisit} />;
    }

    if (meta.maxGroupSize) {
        yield <MetadataItem Icon={GroupIcon} text={meta.maxGroupSize} />;
    }

    if (meta.advanceArrivalDuration) {
        const beforeStartTimeLabel = t("label.beforeStartTime");
        const durationFormattedText = `${formatDurationToTextAlternative(
            meta.advanceArrivalDuration
        )} ${beforeStartTimeLabel}`;
        yield <MetadataItem Icon={UpdateIcon} text={durationFormattedText} />;
    }

    if (attributeList.length > 0) {
        const attributes = computeAttributes(attributeList.filter((al) => al.level1 !== VOUCHER_LEVEL1)).filter(
            ({ names }) => names.some((name) => !!name)
        );

        for (const attribute of attributes) {
            yield <MetadataItem key={attribute.label} Icon={attribute.icon} text={attribute.names.join(", ")} />;
        }
    }

    if (!isOnRequest) {
        yield <MetadataItem Icon={ElectricBoltIcon} text={t("productMeta.instantConfirmation")} />;
    }

    yield <MetadataItem Icon={VolunteerActivismIcon} text={t("productMeta.travellersLove")} />;

    yield <MetadataItem Icon={RecommendIcon} text={t("productMeta.recommended")} />;
}

function splitVoucherFromAttributeList(attributeList?: ProductAttributeListNode[]) {
    if (!attributeList || attributeList.length === 0) return { voucher: undefined, attributeList: [] };

    const voucher = attributeList.find((al) => al.level1 === VOUCHER_LEVEL1);

    return {
        voucher,
        attributeList: attributeList.filter((al) => al.level1 !== VOUCHER_LEVEL1),
    };
}

type MetadataItemProps = { Icon: ComponentType<SvgIconProps>; text?: string };
export function MetadataItem(props: MetadataItemProps) {
    const { Icon, text } = props;
    return (
        <ItemWrapper>
            <Icon />
            <TypographyStyled color="subtle" isResponsive={true}>
                {text}
            </TypographyStyled>
        </ItemWrapper>
    );
}

const ItemWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    flex: 1,
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
}));

const TypographyStyled = styled(Typography)({
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
});

export function ProductGuideLanguageMetadata({ languages }: { languages: ProductGuideLanguageListNode[] }) {
    const value = useLanguageValue(languages);

    return <MetadataItem Icon={LanguageIcon} text={value} />;
}

function useLanguageValue(guideLanguages: ProductGuideLanguageListNode[]) {
    const [t] = useProductTranslation();

    if (guideLanguages.length > 1) {
        return t("Multiple");
    } else if (guideLanguages.length == 1) {
        return capitalize(guideLanguages[0].name ?? "");
    }

    return;
}

const ATTRIBUTES_ICONS_BY_LEVEL: Record<string, ComponentType<SvgIconProps> | undefined> = {
    Style: StyleIcon,
    "Traveler Type": PeopleIcon,
    "Time Required": UpdateIcon,
    "Season / Occasion": WbSunnyIcon,
    Language: LanguageIcon,
    "Added Extras": AddIcon,
    "Food and Drink Included ____": FastfoodIcon,
    Accessibility: AccessibleIcon,
    Transport: DirectionsCarIcon,
    "Meeting Type": FlagIcon,
};

function computeAttributes(
    attributes: ProductAttributeListNode[]
): Array<{ label: string; names: string[]; icon: ComponentType<SvgIconProps> }> {
    const attributesByLevel1 = groupBy(attributes, "level1");

    return Object.entries(attributesByLevel1).map(([level1, attributes]) => {
        const names = attributes.map(({ name }) => `${capitalize(name ?? "")}`);
        const icon = ATTRIBUTES_ICONS_BY_LEVEL[level1] ?? FlareIcon;

        return {
            label: level1,
            names,
            icon,
        };
    });
}
